import { Link } from "react-router-dom";
import { Instagram, Phone, MapPin, Clock, MessageCircle } from "lucide-react";
import { useZeoContent } from "../lib/ZeoContentContext";
import { useProducts } from "../lib/ProductsContext";
import { emotions } from "../data/siteData";

// Kart ödemesi kabul edildiğini belirten güven rozetleri — resmi marka
// logoları (bkz. public/payments/ ve public/payment-logos/), her sağlayıcının
// kendi marka kurallarına uygun şekilde, sadece kabul edilen ödeme
// yöntemlerini belirtmek amacıyla gösterilir. PayTR/iyzico logoları
// sağlayıcıların kendi resmi marka paketlerinden alındı.
function PaymentBadges() {
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="footer-payment-badges" aria-label="Kabul edilen kartlar ve ödeme sağlayıcıları: Visa, Mastercard, Troy, PayTR, iyzico">
      <span className="h-7 px-2.5 rounded-md bg-white flex items-center justify-center shrink-0 shadow-sm">
        <img src="/payments/visa.svg" alt="Visa" className="h-3" />
      </span>
      <span className="h-7 px-2 rounded-md bg-white flex items-center justify-center shrink-0 shadow-sm">
        <img src="/payments/mastercard.svg" alt="Mastercard" className="h-5" />
      </span>
      <span className="h-7 px-2.5 rounded-md bg-white flex items-center justify-center shrink-0 shadow-sm">
        <img src="/payments/troy.png" alt="Troy" className="h-3.5" />
      </span>
      <span className="h-7 px-2.5 rounded-md bg-white flex items-center justify-center shrink-0 shadow-sm">
        <img src="/payment-logos/paytr.png" alt="PayTR" className="h-3" />
      </span>
      <span className="h-7 px-2.5 rounded-md bg-white flex items-center justify-center shrink-0 shadow-sm">
        <img src="/payment-logos/iyzico-horizontal.svg" alt="iyzico" className="h-3.5" />
      </span>
    </div>
  );
}

const STATIC_LINK_GROUPS = [
  {
    title: "Kurumsal",
    links: [
      { label: "Hakkımızda", to: "/hakkimizda" },
      { label: "İletişim", to: "/bize-ulasin" },
      { label: "Mağazamız", to: "/magazamiz" },
      { label: "Sıkça Sorulan Sorular", to: "/sss" },
    ],
  },
  {
    title: "Sözleşmeler",
    links: [
      { label: "Mesafeli Satış Sözleşmesi", to: "/bilgi/mesafeli-satis-sozlesmesi" },
      { label: "Gizlilik Politikası", to: "/bilgi/gizlilik-politikasi" },
      { label: "Çerez Politikası", to: "/bilgi/cerez-politikasi" },
      { label: "İade ve Değişim Politikası", to: "/bilgi/iade-politikasi" },
      { label: "Üyelik Sözleşmesi", to: "/bilgi/uyelik-sozlesmesi" },
      { label: "KVKK Aydınlatma Metni", to: "/bilgi/kvkk-aydinlatma-metni" },
    ],
  },
  {
    title: "Hesabım",
    links: [
      { label: "Hesabım", to: "/hesabim/siparislerim" },
      { label: "Siparişlerim", to: "/hesabim/siparislerim" },
      { label: "Sipariş Takip", to: "/siparis-takibi" },
    ],
  },
];

export default function Footer() {
  const { business } = useZeoContent();
  // GÜVENLİK/DENETİM DÜZELTMESİ: bu iki grup daha önce modül seviyesinde,
  // statik demo `categories`'ten (siteData.js) ve elle yazılmış eski
  // kategori slug'larından (anneler-gunu, sevgiliye, dogum-gunu...)
  // üretiliyordu — RoseBox kategori taşınması sonrası bu slug'lar artık
  // gerçek kategorilerle eşleşmiyor, her footer linki "Kategori bulunamadı"
  // sayfasına düşerdi. Header'daki gibi CANLI `categories`/`emotions`
  // üzerinden kuruluyor.
  const { categories } = useProducts();
  const topCategories = categories.filter((c) => !c.parentId).slice(0, 8);
  const linkGroups = [
    STATIC_LINK_GROUPS[0],
    {
      title: "Kategoriler",
      links: topCategories.map((c) => ({ label: c.name, to: `/kategori/${c.id}` })),
    },
    STATIC_LINK_GROUPS[1],
    STATIC_LINK_GROUPS[2],
    {
      title: "Vesileye Göre",
      links: [
        ...emotions.map((e) => ({ label: e.label, to: e.to })),
        { label: "Gönderim Bölgeleri", to: "/bilgi/teslimat-bilgileri" },
      ],
    },
  ];
  return (
    <footer id="iletisim" className="bg-[#222222] text-white" data-testid="site-footer">
      {/* Newsletter strip */}
      <div className="border-b border-white/10">
        <div className="frida-container py-12 md:py-16 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <h3 className="font-serif text-3xl lg:text-4xl tracking-tight leading-tight" data-testid="newsletter-title">
              Premium Bültenimize <span className="italic text-[#D4A15C]">Katılın</span>
            </h3>
            <p className="mt-3 text-white/70 max-w-md">
              Yeni koleksiyonlar ve özel kampanyalardan ilk siz haberdar olun.
            </p>
          </div>
          <form className="flex flex-col sm:flex-row gap-3" onSubmit={(e) => e.preventDefault()} data-testid="newsletter-form">
            <input
              type="email"
              required
              placeholder="E-posta adresiniz"
              className="flex-1 px-6 py-4 bg-white/10 backdrop-blur-md text-white placeholder:text-white/50 border border-white/15 rounded-full outline-none focus:border-[#D4A15C] transition-colors"
              data-testid="newsletter-input"
            />
            <button
              type="submit"
              className="px-7 py-4 bg-[#D4A15C] hover:bg-[#B8863F] text-[#222222] rounded-full font-medium transition-colors active:scale-95"
              data-testid="newsletter-submit"
            >
              Abone Ol
            </button>
          </form>
        </div>
      </div>

      {/* Main footer grid */}
      <div className="frida-container py-16 md:py-20 grid grid-cols-2 md:grid-cols-7 gap-10 lg:gap-12">
        <div className="col-span-2 md:col-span-2">
          <div className="flex items-center gap-4 mb-6">
            <img src="/frida-cicekcilik-logo.png" alt="Frida Çiçekçilik" width="80" height="80" className="w-20 h-20 object-contain shrink-0" />
            <div>
              <div className="font-serif text-2xl font-bold">Frida Çiçekçilik</div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-white/60 mt-1">
                Çiçekle Duygularınızı Anlatın
              </div>
            </div>
          </div>

          <p className="text-white/70 leading-relaxed mb-6 max-w-sm">
            Hatay merkezli premium çiçekçi. Her özel anınız için profesyonel tasarım ve aynı gün teslimat.
          </p>

          <ul className="space-y-3 text-sm text-white/80">
            <li className="flex items-start gap-3" data-testid="footer-address">
              <MapPin className="w-4 h-4 text-[#D4A15C] mt-1 shrink-0" />
              <span>{business.address}</span>
            </li>
            <li className="flex items-center gap-3" data-testid="footer-phone">
              <Phone className="w-4 h-4 text-[#D4A15C] shrink-0" />
              <a href={`tel:${business.phoneRaw}`} className="hover:text-white">{business.phone}</a>
            </li>
            <li className="flex items-center gap-3" data-testid="footer-hours">
              <Clock className="w-4 h-4 text-[#D4A15C] shrink-0" />
              <span>{business.hours}</span>
            </li>
          </ul>

          <div className="flex items-center gap-3 mt-7">
            <a
              href={business.social.instagram}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#D4A15C] hover:text-[#222222] flex items-center justify-center transition-colors"
              data-testid="footer-instagram"
            >
              <Instagram className="w-4 h-4" />
            </a>
            <a
              href={business.social.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp"
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#25D366] flex items-center justify-center transition-colors"
              data-testid="footer-whatsapp"
            >
              <MessageCircle className="w-4 h-4" />
            </a>
          </div>
        </div>

        {linkGroups.filter((g) => g.links.length > 0).map((g) => (
          <div key={g.title} className="md:col-span-1">
            <h4 className="font-serif text-base mb-5 text-white">{g.title}</h4>
            <ul className="space-y-3">
              {g.links.map((l) => (
                <li key={l.label}>
                  <Link to={l.to} className="text-sm text-white/65 hover:text-[#D4A15C] transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Bottom strip */}
      <div className="border-t border-white/10">
        <div className="frida-container py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/55">
          <p>© {new Date().getFullYear()} Frida Çiçekçilik. Tüm hakları saklıdır.</p>
          <div className="flex items-center gap-5">
            <PaymentBadges />
            <span>★ 5.0 / 150+ Google Yorumu</span>
          </div>
        </div>
      </div>

      {/* Credit strip */}
      <div className="border-t border-white/10">
        <div className="frida-container py-5 flex items-center justify-center gap-1.5 text-center text-xs text-white/50">
          Bu site{" "}
          <a
            href="https://zeomedya.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-[#D4A15C] hover:text-white transition-colors font-medium"
            data-testid="footer-zeomedya-link"
          >
            <img src="/zeomedya-icon.png" alt="" width="14" height="14" className="opacity-90" />
            ZEO Medya
          </a>{" "}
          tarafından yapılmıştır.
        </div>
      </div>
    </footer>
  );
}
